'use client'

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useExtracted, useFormatter } from 'next-intl'
import { Button } from '@/components/ui/button'
import { X } from '@/components/ui/icons'
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group'
import { previewTeamCheckout } from '@/lib/api'
import { isBackendRouteUnavailableError, userFacingErrorMessage } from '@/lib/apiErrors'
import { captureSentryError } from '@/lib/sentry'
import type { TeamCheckoutQuote } from '@/types/shared'

interface TeamCheckoutDiscountProps {
  planId: string
  quote: TeamCheckoutQuote | null
  onChange: (quote: TeamCheckoutQuote | null) => void
  onReadyChange: (ready: boolean) => void
  disabled?: boolean
  preview?: (planId: string, promotionCode?: string) => Promise<TeamCheckoutQuote>
  previewOperation?: string
  /** Skip the automatic-offer preview until the user chooses to enter a code. */
  optional?: boolean
  /** A code handed over by URL (`/pricing?code=…`); applied once on mount when nothing is selected yet. */
  initialCode?: string
  /** Hide the first-month total when the surrounding card already shows the price. */
  hideTotal?: boolean
  /** Restore local field state when the picker remounts (select-plan CTA swap). */
  ui?: { expanded: boolean; draft: string; error: string | null }
  onUiChange?: (ui: { expanded: boolean; draft: string; error: string | null }) => void
}

export function TeamCheckoutDiscount({
  planId,
  quote,
  onChange,
  onReadyChange,
  disabled,
  preview = previewTeamCheckout,
  previewOperation = 'team_checkout_preview',
  optional = false,
  initialCode,
  hideTotal = false,
  ui,
  onUiChange,
}: TeamCheckoutDiscountProps) {
  const t = useExtracted()
  const format = useFormatter()
  const inputId = useId()
  const requestId = useRef(0)
  const [skipInitialLoad] = useState(() => optional || Boolean(quote))
  // A URL code is applied once, on mount, and only when nothing is selected yet.
  const [pendingInitialCode] = useState(() => (!quote && initialCode?.trim() ? initialCode.trim() : null))
  const [expanded, setExpanded] = useState(ui?.expanded ?? false)
  const [draft, setDraft] = useState(() =>
    ui?.expanded ? ui.draft : (quote?.promotionCode ?? ui?.draft ?? pendingInitialCode ?? ''),
  )
  const [busy, setBusy] = useState(!skipInitialLoad || Boolean(pendingInitialCode))
  const [error, setError] = useState<string | null>(ui?.error ?? null)
  const [routeUnavailable, setRouteUnavailable] = useState(false)

  const load = useCallback(
    async (code?: string) => {
      const id = ++requestId.current
      try {
        const result = await preview(planId, code)
        if (requestId.current !== id) return
        setRouteUnavailable(false)
        onChange(result)
        setDraft(result.promotionCode ?? '')
        setExpanded(false)
      } catch (err) {
        if (requestId.current !== id) return
        if (isBackendRouteUnavailableError(err)) {
          setRouteUnavailable(true)
          return
        }
        setRouteUnavailable(false)
        captureSentryError(err, { operation: previewOperation })
        setError(userFacingErrorMessage(err, t('We could not check this price. Please try again.')))
        // A rejected code (URL hand-off or typed) stays visible with the reason instead of collapsing.
        if (code) setExpanded(true)
      } finally {
        if (requestId.current === id) setBusy(false)
      }
    },
    [planId, onChange, t, preview, previewOperation],
  )

  useEffect(() => {
    onUiChange?.({ expanded, draft, error })
  }, [expanded, draft, error, onUiChange])

  useEffect(() => {
    if (skipInitialLoad) return
    const requests = requestId
    // Initial price fetch; state updates happen only after the network response.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load(pendingInitialCode ?? undefined)
    return () => {
      requests.current++
    }
  }, [load, skipInitialLoad, pendingInitialCode])

  useEffect(() => {
    if (!skipInitialLoad) return
    const requests = requestId
    // Same shape as the initial price fetch above: state updates only land after the network response.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (pendingInitialCode) void load(pendingInitialCode)
    return () => {
      requests.current++
    }
  }, [skipInitialLoad, pendingInitialCode, load])

  const apply = (code?: string) => {
    setBusy(true)
    setError(null)
    void load(code)
  }

  const clearOptionalCode = () => {
    onChange(null)
    setDraft('')
    setExpanded(false)
    setError(null)
    setBusy(false)
  }

  const hasUnappliedCode = expanded && draft.trim() !== (quote?.promotionCode ?? '')
  useEffect(() => {
    if (optional) {
      onReadyChange(!busy && (routeUnavailable ? !quote?.promotionCode : !expanded))
      return
    }
    // On an older backend, preserve the original checkout flow. An already
    // selected custom code must be explicitly removed before that fallback.
    onReadyChange(!busy && (routeUnavailable ? !quote?.promotionCode : Boolean(quote) && !hasUnappliedCode))
  }, [optional, quote, busy, expanded, hasUnappliedCode, routeUnavailable, onReadyChange])

  if (routeUnavailable) {
    return (
      <div className="space-y-2 pt-2">
        <p className="text-sm text-muted-foreground" role="status">
          {t('Coupon entry is temporarily unavailable. Try again, or continue without a custom code.')}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={disabled || busy}
            onClick={() => apply(quote?.promotionCode)}
          >
            {t('Try again')}
          </Button>
          {quote?.promotionCode && (
            <Button
              type="button"
              variant="outline"
              disabled={disabled || busy}
              onClick={() => {
                onChange(null)
                setDraft('')
                setExpanded(false)
                setError(null)
              }}
            >
              {t('Remove {code}', { code: quote.promotionCode })}
            </Button>
          )}
        </div>
      </div>
    )
  }

  const money = (amount: number) =>
    format.number(amount, {
      style: 'currency',
      currency: quote!.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  const discounted = quote && quote.amount < quote.regularAmount
  const percent = quote ? Math.round((1 - quote.amount / quote.regularAmount) * 100) : 0

  const offerLabel =
    quote?.promotionCode ??
    (discounted ? t('{percent}% off your first month', { percent: String(percent) }) : t('Promo code'))

  const couponField = (
    <>
      <label htmlFor={inputId} className="sr-only">
        {t('Promo code')}
      </label>
      <div className="flex w-full items-center gap-1">
        <InputGroup>
          <InputGroupInput
            id={inputId}
            className="min-w-0"
            data-quiet-focus
            value={draft}
            autoFocus
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            placeholder={t('Enter code')}
            disabled={disabled || busy}
            maxLength={200}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${inputId}-error` : undefined}
            onChange={event => {
              setDraft(event.target.value)
              setError(null)
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault()
                event.stopPropagation()
                if (draft.trim() && !busy) apply(draft.trim())
              }
            }}
          />
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              size="sm"
              disabled={disabled || busy || !draft.trim()}
              onClick={() => apply(draft.trim())}
            >
              {busy ? t('Checking...') : t('Apply')}
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 text-muted-foreground"
          disabled={disabled || busy}
          onClick={() => {
            setDraft(quote?.promotionCode ?? '')
            setExpanded(false)
            setError(null)
          }}
        >
          <X aria-hidden="true" />
          <span className="sr-only">{t('Cancel')}</span>
        </Button>
      </div>
      {((!hideTotal && (discounted || quote?.promotionCode)) || quote?.promotionCode) && (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {!hideTotal && <span className="break-all">{offerLabel}</span>}
          {quote?.promotionCode && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs text-muted-foreground"
              disabled={disabled || busy}
              onClick={() => (optional ? clearOptionalCode() : apply())}
            >
              {t('Use automatic offer')}
            </Button>
          )}
        </div>
      )}
    </>
  )

  const picker = (
    <>
      {optional && !quote && !expanded && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-xs"
          disabled={disabled || busy}
          onClick={() => {
            setExpanded(true)
            setError(null)
          }}
        >
          {t('Have a coupon?')}
        </Button>
      )}
      {expanded && couponField}
      {!expanded && quote && (
        <div className={`flex items-center justify-between gap-3 ${optional ? 'w-full' : ''}`}>
          <div className="min-w-0 text-sm">
            <p
              className={
                quote.promotionCode
                  ? 'inline-block max-w-full truncate rounded-md border border-border px-2 py-0.5 font-mono text-xs font-medium'
                  : 'truncate font-medium'
              }
            >
              {offerLabel}
            </p>
            {!optional && (discounted || quote.promotionCode) && (
              <p className="mt-1 text-xs text-muted-foreground">
                {quote.promotionCode ? t('Coupon applied') : t('Applied automatically')}
              </p>
            )}
          </div>
          <Button type="button" variant="ghost" size="sm" disabled={disabled || busy} onClick={() => setExpanded(true)}>
            {/* 'Use a code' is a plain literal on purpose: feature PRs leave new copy for the i18n sweep. */}
            {quote.promotionCode ? t('Change') : discounted ? 'Use a code' : t('Add code')}
          </Button>
        </div>
      )}
      {optional && quote && !expanded && (
        <span className="sr-only">{quote.promotionCode ? t('Coupon applied') : t('Applied automatically')}</span>
      )}
    </>
  )

  return (
    <div className={optional ? undefined : 'space-y-3 pt-2'}>
      {optional ? <div className="flex min-h-9 w-full items-center">{picker}</div> : picker}
      {error && (
        <p id={`${inputId}-error`} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {!optional && !quote && !expanded && (
        <div className="text-sm text-muted-foreground" role="status">
          {busy ? (
            t('Checking price...')
          ) : (
            <Button type="button" variant="outline" onClick={() => apply()}>
              {t('Try again')}
            </Button>
          )}
        </div>
      )}
      {quote && !hideTotal && (
        <div className="border-t border-border pt-3" aria-live="polite">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm">{t('First month')}</span>
            <span className="text-2xl font-semibold tabular-nums">{money(quote.amount)}</span>
          </div>
          <p className="mt-1 text-end text-xs text-muted-foreground">
            {quote.discountMonths === null
              ? t('{price}/month', { price: money(quote.amount) })
              : quote.discountMonths > 1
                ? t('{price}/month for {months} months, then {regularPrice}/month', {
                    price: money(quote.amount),
                    months: String(quote.discountMonths),
                    regularPrice: money(quote.regularAmount),
                  })
                : t('Then {price}/month', { price: money(quote.regularAmount) })}
          </p>
        </div>
      )}
    </div>
  )
}
